import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Plus, MoreHorizontal, FileText, Edit, Trash2, MapPin } from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { AddEquipmentToLocationSheet } from "./AddEquipmentToLocationSheet";
import { MaintenanceLogSheet } from "../../equipment/MaintenanceLogSheet";
import { formatCurrency } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface LocationEquipmentTabProps {
  locationId: Id<"serviceLocations">;
}

export function LocationEquipmentTab({ locationId }: LocationEquipmentTabProps) {
  const [isAddEquipmentOpen, setIsAddEquipmentOpen] = useState(false);
  const [maintenanceLogEquipment, setMaintenanceLogEquipment] = useState<Id<"equipment"> | null>(
    null
  );
  const [equipmentToUnassign, setEquipmentToUnassign] = useState<{ id: Id<"equipment">; name: string } | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const equipment = useQuery(api.equipment.getByLocation, { locationId }) || [];
  const unassignEquipment = useMutation(api.equipment.unassignFromLocation);

  const handleUnassign = async () => {
    if (!equipmentToUnassign) return;
    setIsUnassigning(true);
    try {
      await unassignEquipment({ equipmentId: equipmentToUnassign.id });
      toast.success("Equipment unassigned successfully");
      setEquipmentToUnassign(null);
    } catch (error) {
      toast.error("Failed to unassign equipment");
    } finally {
      setIsUnassigning(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "In Service":
        return "bg-green-100 text-green-800 border-green-200 hover:bg-green-100";
      case "Under Repair":
        return "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100";
      case "Retired":
        return "bg-red-100 text-red-800 border-red-200 hover:bg-red-100";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-100";
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Equipment</h2>
          <Button className="gap-2" onClick={() => setIsAddEquipmentOpen(true)}>
            <Plus className="h-4 w-4" /> Assign Equipment
          </Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Equipment Name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Serial Number</TableHead>
              <TableHead>Purchase Date</TableHead>
              <TableHead>Purchase Price</TableHead>
              <TableHead>Warranty Expiration</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {equipment.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center text-gray-500">
                  No equipment assigned to this location yet.
                </TableCell>
              </TableRow>
            ) : (
              equipment.map((item: any) => (
                <TableRow key={item._id}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.brand}</TableCell>
                  <TableCell>{item.model}</TableCell>
                  <TableCell>{item.serialNumber || "-"}</TableCell>
                  <TableCell>{formatDate(item.purchaseDate)}</TableCell>
                  <TableCell>{formatCurrency(item.purchasePrice)}</TableCell>
                  <TableCell>{formatDate(item.warrantyExpirationDate)}</TableCell>
                  <TableCell>
                    <Badge className={getStatusBadgeClass(item.status)}>{item.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMaintenanceLogEquipment(item._id)}
                        className="gap-1"
                      >
                        <FileText className="h-3 w-3" />
                        Log
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setEquipmentToUnassign({ id: item._id, name: item.name })}
                            className="text-red-600"
                          >
                            <MapPin className="mr-2 h-4 w-4" />
                            Unassign from Location
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <AddEquipmentToLocationSheet
        isOpen={isAddEquipmentOpen}
        onClose={() => setIsAddEquipmentOpen(false)}
        locationId={locationId}
      />

      {maintenanceLogEquipment && (
        <MaintenanceLogSheet
          equipmentId={maintenanceLogEquipment}
          isOpen={!!maintenanceLogEquipment}
          onClose={() => setMaintenanceLogEquipment(null)}
        />
      )}

      <AlertDialog
        open={!!equipmentToUnassign}
        onOpenChange={(open) => !open && setEquipmentToUnassign(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to unassign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {equipmentToUnassign?.name} from this location. You can assign it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnassign}
              disabled={isUnassigning}
              className="bg-red-600 hover:bg-red-700"
            >
              {isUnassigning ? "Unassigning..." : "Unassign"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
